import React from "react";
// Components
import { TableBody, TableHead } from "./components";
// Hooks
import useActions from "hooks/useActions";
// Store Utils
import { categoriesSortingAction, importsSortingAction } from "store/filters";
import { useDispatch } from "react-redux";
// Utils
import exportDataToCsv from "utils/exportDataToCsv";
import getAllRowsData from "utils/getAllRowsData";
// Exteranl Tools
import { snakeCase } from "change-case";
// Styling
import "./Communities.scss";
// types
import { TableProps } from "./types";
import apiLibrary from "services/api";
import { Toasts } from "view/components/Toasts";
import { filterKeys } from "Components/Users/Table";
import { RootState } from "store";
import { filtersInitialState } from "store/filters/initialState";
import { useSelector } from "react-redux";

export const KEYS_TO_BE_REMOVED = ["id"];
const generatingDirectoryActions = (columns: any, rowsData: any) => {
  const { importsFilter } = useSelector<RootState, filtersInitialState>(
    (state) => state.Filters
  );

  const fileDownload = async () => {
    if (rowsData.length === 0) {
      Toasts.error(
        `Please select at least one ${importsFilter.status === "permission" ? "admin" : "import"} from the table`
      );
      return;
    }

    try {
      const res = await apiLibrary.file.fileDownload(
        importsFilter.status === "permission" ? "import_permission" : "imports",
        columns,
        rowsData.map((item: any) => {
          return importsFilter.status === "permission"
            ? item.name.personId
            : item.fileName.id;
        })
      );
      // Split the response into rows and columns (assuming res is a CSV string)
      const rows = res.split("\n");

      // Get the headers (first row)
      const headers = rows[0].split(",");

      // Function to format date-time values for Excel compatibility
      const formatDateForExcel = (dateString: any): string => {
        if (!dateString) return dateString;

        // Check if the string contains both date and time with microseconds
        const dateRegex = /\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{6}/;

        if (dateRegex.test(dateString)) {
          // Remove the microseconds part after the dot (.)
          const [datePart, timePart] = dateString.split(" ");
          const formattedDateTime = `${datePart} ${timePart.split(".")[0]}`; // Remove everything after the dot
          return formattedDateTime;
        }

        return dateString; // Return as is if it's not a valid date-time
      };

      // Format data (date-time values for Excel)
      const formattedData = rows.slice(1).map((line: any) => {
        const columns = line.split(",");
        return columns.map((column: any) => formatDateForExcel(column)); // Format date-time columns
      });

      // Convert the formatted data into CSV
      const csvContent = [
        headers.join(","), // Header row
        ...formattedData.map((row: any) => row.join(",")), // Data rows
      ].join("\n");

      // Convert the formatted data to a Blob
      const blob = new Blob([csvContent], { type: "application/octet-stream" });

      // Create a link element for the download
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);

      // Use new Date() to get the current timestamp and format it directly
      const timestamp = new Date().toISOString(); // e.g., "2025-04-04T10:36:51.000Z"

      // Set the download attribute to specify the file name (using the timestamp as is)
      link.download = `imports_${timestamp}.csv`;

      // Append the link to the body
      document.body.appendChild(link);

      // Programmatically click the link to trigger the download
      link.click();

      // Remove the link from the DOM
      document.body.removeChild(link);

      Toasts.success("Download complete");
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error?.message;
      Toasts.error(errorMsg);
    }
  };

  return [
    {
      label: "Export Details",
      onClick: () => fileDownload(),
      divider: false,
      isVisible: true,
    },
  ];
};

export const Table: React.FC<TableProps> = ({
  table,
  columnOrder,
  columns,
  columnVisibility,
  flexRender,
  setSorting,
  sorting,
}) => {
  const dispatch = useDispatch();
  const { importActions } = useActions();
  const { importsFilter } = useSelector<RootState, filtersInitialState>(
    (state) => state.Filters
  );

  const DirectoryActions = () => {
    const filteredKeys = filterKeys(columnVisibility, columnOrder);
    return generatingDirectoryActions(
      filteredKeys,
      getAllRowsData(table, columnOrder, columnVisibility, columns)
    );
  };

  const getImportActions = (data: any) => {
    const modalData = {
      id: data?.id,
    };
    return importActions(modalData);
  };

  const toggleSorting = (columnId: string) => {
    setSorting((currentSorting: any) => {
      const existingSort = currentSorting.find(
        (sort: any) => sort.columnId === columnId
      );
      const columnsId = snakeCase(columnId);
      if (!existingSort) {
        dispatch(importsSortingAction(columnsId, "asc"));

        return [{ columnId, type: "asc" }];
      } else if (existingSort.type === "asc") {
        dispatch(importsSortingAction(columnsId, "desc"));

        return [{ columnId, type: "desc" }];
      } else {
        dispatch(importsSortingAction("", ""));

        return [];
      }
    });
  };
  const handleClickOnTableColumnHeading = (header: any) =>
    header.column.getCanSort() && toggleSorting(header.column.id);
  return (
    <div className="w-full overflow-hidden max-h-[80vh] overflow-x-auto overflow-y-auto">
      <table className="w-full">
        <TableHead
          table={table}
          sorting={sorting}
          flexRender={flexRender}
          DirectoryActions={DirectoryActions()}
          handleClickOnColumnHead={handleClickOnTableColumnHeading}
        />
        <TableBody
          flexRender={flexRender}
          table={table}
          columnVisibility={columnVisibility}
          getImportActions={getImportActions}
        />
      </table>
    </div>
  );
};
